/**
 * Cascading geo filter — Country / Region / City, with an optional Network level.
 * Shared by the Route Map and Pulse pages.
 *
 * Single-select per level; the levels are independent and you may start at ANY
 * level (a lone region, or a city then nothing else) — each select shows its full
 * catalog by default and narrows once a parent is chosen. Chain gating keeps the
 * selection one contiguous Country→Region→City[→Network] run (no skipped level).
 * Region/city/network are stored as `country:code` composite keys so they stay
 * unambiguous even when no country is chosen; `geoFilterToApi` splits them back
 * out (inferring the parent country / a city's parent region the API requires).
 */
import { useEffect, useMemo } from 'react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import {
  geoCountries,
  geoRegions,
  geoCities,
  geoNetworks,
  regionsOf,
  countryName,
  humanizeGeoName,
} from './geo';
import styles from './WafGeoFilter.module.css';

/** Radix Select can't hold an empty string value, so "Any" gets a sentinel. */
const ANY = '__any__';

/** Split a `country:code` composite key. */
export const splitGeoKey = (k: string): [string, string] => {
  const i = k.indexOf(':');
  return [k.slice(0, i), k.slice(i + 1)];
};

interface GeoOption {
  value: string;
  label: string;
}

/** A labelled single-select with a leading "Any" option. `value === ''` = any. */
function FilterSelect({
  label,
  placeholder,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  options: GeoOption[];
  disabled?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <div className={styles.filterCell}>
      <span className={styles.filterLabel}>{label}</span>
      <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? '' : v)} disabled={disabled}>
        <SelectTrigger aria-label={label}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{placeholder}</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export interface GeoFilterValue {
  country: string;
  region: string;
  city: string;
  network: string;
}

/**
 * Split the composite selections into the flat country/region/city/network codes
 * the API expects. Keys: country = `code`; region/network = `country:code`; city =
 * `country:region:code` (region is part of the city key so it stays unique across
 * the full catalog). The parent country/region are taken from the composite key
 * even when the user started below the country level.
 */
export function geoFilterToApi(country: string, region: string, city: string, network = ''): GeoFilterValue {
  const cityParts = city ? city.split(':') : [];
  const cityCountry = cityParts[0] ?? '';
  const cityRegion = cityParts.length >= 3 ? cityParts[1] : '';
  const effCity = cityParts.length >= 3 ? cityParts[2] : (cityParts[1] ?? '');

  const effCountry =
    country ||
    (region ? splitGeoKey(region)[0] : cityCountry || (network ? splitGeoKey(network)[0] : ''));
  const effRegion = region ? splitGeoKey(region)[1] : cityRegion;
  const effNetwork = network ? splitGeoKey(network)[1] : '';
  return { country: effCountry, region: effRegion, city: effCity, network: effNetwork };
}

export function WafGeoFilter({
  country,
  region,
  city,
  network = '',
  setCountry,
  setRegion,
  setCity,
  setNetwork,
  showNetwork = true,
  singleLevel = false,
  disabled,
}: {
  country: string;
  region: string;
  city: string;
  network?: string;
  setCountry: (v: string) => void;
  setRegion: (v: string) => void;
  setCity: (v: string) => void;
  setNetwork?: (v: string) => void;
  /** Show the 4th Network level (Route Map). Off for geo-only filters (Pulse). */
  showNetwork?: boolean;
  /** Only one level may be selected at a time (picking one clears the others). */
  singleLevel?: boolean;
  disabled?: boolean;
}) {
  // Build `{value,label}` options from a catalog slice; prefix the country name
  // when the slice spans more than one country (so codes stay unambiguous).
  const toOpts = (items: { country: string; code: string; name: string }[]): GeoOption[] => {
    const multi = new Set(items.map((i) => i.country)).size > 1;
    return items.map((i) => ({
      value: `${i.country}:${i.code}`,
      label: multi ? `${countryName(i.country)} · ${humanizeGeoName(i.name)}` : humanizeGeoName(i.name),
    }));
  };

  const countryOpts = useMemo<GeoOption[]>(
    () => [...geoCountries].map((c) => ({ value: c.code, label: countryName(c.code) })).sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );

  const regionOpts = useMemo<GeoOption[]>(
    () => toOpts(country ? regionsOf(country) : geoRegions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [country],
  );

  const cityOpts = useMemo<GeoOption[]>(() => {
    const list = region
      ? geoCities.filter((c) => `${c.country}:${c.region}` === region)
      : country
        ? geoCities.filter((c) => c.country === country)
        : geoCities;
    // City key includes the region so it stays unique across the full catalog
    // (city codes are only unique within a region, not within a country).
    const multi = new Set(list.map((c) => c.country)).size > 1;
    return list.map((c) => ({
      value: `${c.country}:${c.region}:${c.code}`,
      label: multi ? `${countryName(c.country)} · ${humanizeGeoName(c.name)}` : humanizeGeoName(c.name),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, region]);

  const networkOpts = useMemo<GeoOption[]>(() => {
    const cityCountry = city ? splitGeoKey(city)[0] : '';
    const list = cityCountry
      ? geoNetworks.filter((n) => n.country === cityCountry)
      : country
        ? geoNetworks.filter((n) => n.country === country)
        : geoNetworks;
    return toOpts(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, city]);

  // Prune a selection that fell out of its (now narrower) option set.
  useEffect(() => {
    if (region && !regionOpts.some((o) => o.value === region)) setRegion('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionOpts]);
  useEffect(() => {
    if (city && !cityOpts.some((o) => o.value === city)) setCity('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityOpts]);
  useEffect(() => {
    if (showNetwork && network && !networkOpts.some((o) => o.value === network)) setNetwork?.('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkOpts]);

  // Chain gating — keep the selection one contiguous run; pick a starting level,
  // then only extend downward (levels above the start are locked, no skipping).
  // Disabled in single-level mode, where every level is independently selectable.
  const levelSel = showNetwork ? [country, region, city, network] : [country, region, city];
  const lo = levelSel.findIndex((l) => !!l);
  let blockEnd = lo;
  if (lo >= 0) while (blockEnd + 1 < levelSel.length && levelSel[blockEnd + 1]) blockEnd++;
  const levelEnabled = (i: number) => singleLevel || lo < 0 || (i >= lo && i <= blockEnd) || i === blockEnd + 1;

  // Drop selections stranded past a gap (e.g. clearing City orphans Network).
  useEffect(() => {
    if (singleLevel || lo < 0) return;
    if (city && 2 > blockEnd) setCity('');
    if (showNetwork && network && 3 > blockEnd) setNetwork?.('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lo, blockEnd]);

  // Single-level mode: selecting one level clears the others (mutually exclusive).
  const pickCountry = singleLevel
    ? (v: string) => { setCountry(v); if (v) { setRegion(''); setCity(''); setNetwork?.(''); } }
    : setCountry;
  const pickRegion = singleLevel
    ? (v: string) => { setRegion(v); if (v) { setCountry(''); setCity(''); setNetwork?.(''); } }
    : setRegion;
  const pickCity = singleLevel
    ? (v: string) => { setCity(v); if (v) { setCountry(''); setRegion(''); setNetwork?.(''); } }
    : setCity;
  const pickNetwork = singleLevel
    ? (v: string) => { setNetwork?.(v); if (v) { setCountry(''); setRegion(''); setCity(''); } }
    : (v: string) => setNetwork?.(v);

  return (
    <div className={styles.filters}>
      <FilterSelect label="Country" placeholder="Any country" value={country} options={countryOpts} disabled={disabled || !levelEnabled(0)} onChange={pickCountry} />
      <FilterSelect label="Region" placeholder="Any region" value={region} options={regionOpts} disabled={disabled || !levelEnabled(1)} onChange={pickRegion} />
      <FilterSelect label="City" placeholder="Any city" value={city} options={cityOpts} disabled={disabled || !levelEnabled(2)} onChange={pickCity} />
      {showNetwork && (
        <FilterSelect label="Network" placeholder="Any network" value={network} options={networkOpts} disabled={disabled || !levelEnabled(3)} onChange={pickNetwork} />
      )}
    </div>
  );
}

export default WafGeoFilter;
